import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box, HStack, VStack, Text} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetServiceOrderGroupByIdQuery,
  useGetServiceOrderTypesQuery,
  useGetSettingsServiceOrderVariantsQuery,
  usePatchServiceOrderGroupMutation,
  usePostServiceOrderGroupMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {getOptionsFromAuthorizationProfiles, handleApiError, EMPTY_PLACEHOLDER} from '@dms/shared';

import {ApiError, getApiDateString, parseDate, useNavigate} from 'shared/web';

import {
  workshopServiceOrderGroupDetailFormSchema,
  WorkshopServiceOrderGroupDetailFormType,
} from './schemas/workshopServiceOrderGroupDetailFormSchema';

export function WorkshopServiceOrderGroupDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data: tenant} = useGetTenantQuery();

  const isCreating = isNilOrEmpty(id);

  const {data, isLoading, isError} = useGetServiceOrderGroupByIdQuery(
    {serviceOrderGroupId: id as string},
    {skip: isCreating}
  );
  const {data: authorizationProfiles, isLoading: isAuthorizationProfilesLoading} =
    useGetAuthorizationProfilesQuery(
      {'x-tenant': tenant?.id as string},
      {skip: isNilOrEmpty(tenant)}
    );
  const {data: orderTypes, isLoading: isOrderTypesLoading} = useGetServiceOrderTypesQuery(
    undefined,
    {
      selectFromResult: (result) => ({
        ...result,
        data:
          result.data?.map((orderType) => ({
            label: orderType?.name,
            value: orderType?.id,
          })) ?? [],
      }),
    }
  );
  const {data: orderVariants, isLoading: isOrderVariantsLoading} =
    useGetSettingsServiceOrderVariantsQuery();
  const [postServiceOrderGroupOperation] = usePostServiceOrderGroupMutation();
  const [patchServiceOrderGroupOperation] = usePatchServiceOrderGroupMutation();

  const handleSubmit: FormSubmitHandler<WorkshopServiceOrderGroupDetailFormType> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      ...data,
      name: data.name ?? '',
      validityFrom: data.validityFrom ? getApiDateString(data.validityFrom) : null,
      validityTo: data.validityTo ? getApiDateString(data.validityTo) : null,
      authorizationProfileIds: data.authorizationProfileIds ?? [],
      serviceOrderTypeIds: data.serviceOrderTypeIds ?? [],
      serviceOrderVariantIds: data.serviceOrderVariantIds ?? [],
    };

    const request = isCreating
      ? postServiceOrderGroupOperation({
          body,
        })
      : patchServiceOrderGroupOperation({
          serviceOrderGroupId: id ?? '',
          body,
        });

    return await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.workshopServiceOrderGroups);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const titleKey = isCreating
    ? 'page.settings.actions.newServiceOrderGroup'
    : 'page.settings.labels.serviceOrderGroups';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: isCreating
            ? i18n.t('page.settings.actions.newServiceOrderGroup')
            : (data?.name ?? EMPTY_PLACEHOLDER),
          breadcrumbs: [
            {
              label: i18n.t('page.settings.labels.serviceOrderGroups'),
              href: settingsRoutes.workshopServiceOrderGroups,
            },
          ],
        }}
        isError={isError}
        isLoading={isLoading}
        isCreating={isCreating}
        data-testid={testIds.settings.workshopServiceOrderGroupDetail('template')}
      >
        <Form<WorkshopServiceOrderGroupDetailFormType>
          defaultValues={{
            name: data?.name,
            code: data?.code,
            validityFrom: data?.validityFrom ? parseDate(data?.validityFrom) : null,
            validityTo: data?.validityTo ? parseDate(data?.validityTo) : null,
            authorizationProfileIds: data?.authorizationProfileIds ?? [],
            serviceOrderTypeIds: data?.serviceOrderTypeIds ?? [],
            serviceOrderVariantIds: data?.serviceOrderVariantIds ?? [],
          }}
          onSubmit={handleSubmit}
          experimentalZodSchema={workshopServiceOrderGroupDetailFormSchema}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <SettingsSection>
              <VStack spacing={4}>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="name"
                      type="text"
                      isRequired
                      label={i18n.t('general.labels.name')}
                      data-testid={testIds.settings.workshopServiceOrderGroupDetail('name')}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="code"
                      type="text"
                      label={i18n.t('page.settings.labels.code')}
                      data-testid={testIds.settings.workshopServiceOrderGroupDetail('code')}
                    />
                  </Box>
                </HStack>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="validityFrom"
                      type="date"
                      label={i18n.t('page.settings.labels.validityFrom')}
                      data-testid={testIds.settings.workshopServiceOrderGroupDetail('validityFrom')}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="validityTo"
                      type="date"
                      label={i18n.t('page.settings.labels.validityTo')}
                      data-testid={testIds.settings.workshopServiceOrderGroupDetail('validityTo')}
                    />
                  </Box>
                </HStack>
                <Box>
                  <FormField
                    control={control}
                    name="authorizationProfileIds"
                    type="multiChoice"
                    options={getOptionsFromAuthorizationProfiles(
                      authorizationProfiles,
                      formApi.watch('authorizationProfileIds')
                    )}
                    isLoading={isAuthorizationProfilesLoading}
                    label={i18n.t('entity.settings.labels.authorizationProfile')}
                    data-testid={testIds.settings.workshopServiceOrderGroupDetail(
                      'authorizationProfile'
                    )}
                  />
                  <Text color="secondary" size="xSmall">
                    {i18n.t('entity.settings.labels.authorizationProfileInfo')}
                  </Text>
                </Box>
                <Box>
                  <FormField
                    control={control}
                    name="serviceOrderTypeIds"
                    type="multiChoice"
                    options={orderTypes}
                    isLoading={isOrderTypesLoading}
                    label={i18n.t('entity.settings.labels.serviceOrderTypes')}
                    data-testid={testIds.settings.workshopServiceOrderGroupDetail(
                      'serviceOrderTypes'
                    )}
                  />
                  <Text color="secondary" size="xSmall">
                    {i18n.t('entity.settings.labels.serviceOrderTypesInfo')}
                  </Text>
                </Box>
                <Box>
                  <FormField
                    control={control}
                    name="serviceOrderVariantIds"
                    type="multiChoice"
                    options={orderVariants ?? []}
                    isLoading={isOrderVariantsLoading}
                    label={i18n.t('entity.settings.labels.serviceOrderVariants')}
                    data-testid={testIds.settings.workshopServiceOrderGroupDetail(
                      'serviceOrderVariants'
                    )}
                  />
                  <Text color="secondary" size="xSmall">
                    {i18n.t('entity.settings.labels.serviceOrderVariantsInfo')}
                  </Text>
                </Box>
              </VStack>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    variant: 'secondary',
                    title: i18n.t('general.actions.discard'),
                    onClick: () =>
                      navigateWithoutBlocker(settingsRoutes.workshopServiceOrderGroups),
                  },
                  {
                    control,
                    type: 'form-button',
                    buttonType: 'submit',
                    title: isCreating
                      ? i18n.t('general.actions.create')
                      : i18n.t('general.actions.saveChanges'),
                  },
                ]}
              />
            </SettingsSection>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
